/**
 * Tasks API Service
 * Event Master v1.5 - Backend Integration
 * День 6-7: Tasks Module (20 endpoints)
 * 
 * Интегрированные endpoints:
 * - GET    /api/tasks
 * - POST   /api/tasks
 * - GET    /api/tasks/:id
 * - PUT    /api/tasks/:id
 * - DELETE /api/tasks/:id
 * - PATCH  /api/tasks/:id/status
 * - PATCH  /api/tasks/:id/assign
 * - PATCH  /api/tasks/:id/priority
 * - GET    /api/tasks/:id/comments
 * - POST   /api/tasks/:id/comments
 * - PUT    /api/tasks/:id/comments/:commentId
 * - DELETE /api/tasks/:id/comments/:commentId
 * - GET    /api/tasks/:id/attachments
 * - POST   /api/tasks/:id/attachments
 * - DELETE /api/tasks/:id/attachments/:attachmentId
 * - GET    /api/tasks/my
 * - GET    /api/tasks/assigned
 * - GET    /api/tasks/created
 * - GET    /api/tasks/statistics
 * - GET    /api/tasks/export
 */

import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse, PaginatedResponse } from '../../types/responses/api-response';
import type { TaskTypeValue } from '../../types/events-unified';

// ===== ТИПЫ ДАННЫХ =====

export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  category?: string;
  assigneeId?: string;
  assignee?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  creatorId: string;
  creator?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  eventId?: string;
  event?: {
    id: string;
    title: string;
    startDate: string;
    endDate: string;
  };
  dueDate?: string;
  startDate?: string;
  completedAt?: string;
  estimatedHours?: number;
  actualHours?: number;
  tags: string[];
  attachments: TaskAttachment[];
  comments: TaskComment[];
  dependencies: string[]; // IDs of dependent tasks
  subtasks: string[]; // IDs of subtasks
  labels?: Array<{
    id: string;
    name: string;
    color?: string;
    description?: string;
  }>;
  checklist?: Array<{
    id: string;
    title: string;
    description?: string;
    isCompleted: boolean;
    completedBy?: string;
    completedAt?: string;
  }>;
  progress?: number;
  completionPercentage?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface TaskAttachment {
  id: string;
  taskId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  downloadUrl: string;
  uploadedBy: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
}

export interface TaskStatistics {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  overdue: number;
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  byStatus: {
    pending: number;
    inProgress: number;
    completed: number;
    cancelled: number;
  };
  myTasks: {
    total: number;
    completed: number;
    inProgress: number;
    overdue: number;
  };
  assignedToMe: {
    total: number;
    completed: number;
    inProgress: number;
    overdue: number;
  };
}

// ===== ENUMS =====

// B5a (Вариант B §6): вокабуляр статусов выровнен по BE-канону
// (shared/entities/tasks/task.entity.ts → TaskStatus). Прежние члены PENDING='pending'
// и ON_HOLD='on_hold' в BE-enum отсутствовали → CreateTaskDto.status (@IsEnum, required)
// отбивал их 400 на КАЖДОМ создании через TaskCreatePage (доказано real-PG:
// tasks.controller.create-contract.integration.spec.ts). Канон: todo|in_progress|
// review|blocked|completed|cancelled.
export enum TaskStatus {
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  REVIEW = 'review',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

// ===== REQUEST TYPES =====

export interface CreateTaskRequest {
  title: string;
  description: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  // Тип задачи выбирает человек в форме — без него запрос не собирается (AD-657, №141).
  taskType: TaskTypeValue;
  category?: string;
  assigneeId?: string;
  eventId?: string;
  dueDate?: string;
  startDate?: string;
  estimatedHours?: number;
  tags?: string[];
  dependencies?: string[];
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  category?: string;
  assigneeId?: string;
  eventId?: string;
  dueDate?: string;
  startDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  completedAt?: string;
  progress?: number;
  tags?: string[];
  dependencies?: string[];
}

export interface TaskFilters {
  status?: TaskStatus;
  priority?: TaskPriority;
  category?: string;
  assigneeId?: string;
  creatorId?: string;
  eventId?: string;
  tags?: string[];
  search?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  isOverdue?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'dueDate' | 'priority' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateTaskCommentRequest {
  content: string;
}

export interface UpdateTaskCommentRequest {
  content: string;
}

export interface UploadTaskAttachmentRequest {
  file: File;
  description?: string;
}

// ===== API METHODS =====

export const tasksApi = {
  // ===== УПРАВЛЕНИЕ ЗАДАЧАМИ =====
  
  /**
   * Получить список задач с фильтрацией
   */
  getTasks: async (params?: TaskFilters): Promise<Task[]> => {
    const limit = params?.limit ?? 20;
    const page = params?.page ?? 1;
    const queryParams = {
      status: params?.status,
      priority: params?.priority,
      assignedTo: params?.assigneeId,
      creatorId: params?.creatorId,
      eventId: params?.eventId,
      tags: params?.tags,
      search: params?.search,
      dueDateFrom: params?.dueDateFrom,
      dueDateTo: params?.dueDateTo,
      isOverdue: params?.isOverdue,
      offset: (page - 1) * limit,
      limit,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };

    const response = await httpClient.get<PaginatedResponse<Task> | Task[]>(
      `/tasks${buildQueryString(queryParams)}`,
    );
    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    return payload.data;
  },

  /**
   * Получить задачу по ID
   */
  getTask: async (id: string): Promise<ApiResponse<Task>> => {
    return httpClient.get(`/tasks/${id}`);
  },

  /**
   * Создать новую задачу (Backend: deadline вместо dueDate)
   */
  createTask: async (data: CreateTaskRequest): Promise<ApiResponse<Task>> => {
    const payload: Record<string, unknown> = { ...data };
    if (payload['dueDate'] !== undefined) {
      payload['deadline'] = payload['dueDate'];
      delete payload['dueDate'];
    }
    return httpClient.post('/tasks', payload);
  },

  /**
   * Обновить задачу (Backend: PATCH, deadline вместо dueDate)
   */
  updateTask: async (id: string, data: UpdateTaskRequest): Promise<ApiResponse<Task>> => {
    const payload: Record<string, unknown> = { ...data };
    if (payload['dueDate'] !== undefined) {
      payload['deadline'] = payload['dueDate'];
      delete payload['dueDate'];
    }
    return httpClient.patch(`/tasks/${id}`, payload);
  },

  /**
   * Удалить задачу
   */
  deleteTask: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/tasks/${id}`);
  },

  /**
   * Изменить статус задачи
   */
  updateTaskStatus: async (id: string, status: TaskStatus): Promise<ApiResponse<Task>> => {
    return httpClient.patch(`/tasks/${id}/status`, { status });
  },

  /**
   * Назначить задачу пользователю
   * ВАЖНО: Backend использует POST, не PATCH
   */
  assignTask: async (id: string, assigneeId: string): Promise<ApiResponse<Task>> => {
    return httpClient.post(`/tasks/${id}/assign`, { userId: assigneeId });
  },

  /**
   * Изменить приоритет задачи (Backend: PATCH /tasks/:id с полем priority)
   */
  updateTaskPriority: async (id: string, priority: TaskPriority): Promise<ApiResponse<Task>> => {
    return httpClient.patch(`/tasks/${id}`, { priority });
  },

  // ===== КОММЕНТАРИИ К ЗАДАЧАМ =====

  /**
   * Получить комментарии задачи
   */
  getTaskComments: async (taskId: string): Promise<ApiResponse<TaskComment[]>> => {
    return httpClient.get(`/tasks/${taskId}/comments`);
  },

  /**
   * Добавить комментарий к задаче
   */
  createTaskComment: async (taskId: string, data: CreateTaskCommentRequest): Promise<ApiResponse<TaskComment>> => {
    return httpClient.post(`/tasks/${taskId}/comments`, data);
  },

  /**
   * Обновить комментарий
   */
  updateTaskComment: async (_taskId: string, commentId: string, data: UpdateTaskCommentRequest): Promise<ApiResponse<TaskComment>> => {
    return httpClient.patch(`/tasks/comments/${commentId}`, { content: data.content });
  },

  /**
   * Удалить комментарий
   */
  deleteTaskComment: async (_taskId: string, commentId: string): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/tasks/comments/${commentId}`);
  },

  // ===== ВЛОЖЕНИЯ К ЗАДАЧАМ =====
  // NOT_IMPLEMENTED_BACKEND: endpoints attachments/bulk могут вернуть 404; в UI обрабатывать 404 или не вызывать до появления API.

  /**
   * Получить вложения задачи
   */
  getTaskAttachments: async (taskId: string): Promise<ApiResponse<TaskAttachment[]>> => {
    return httpClient.get(`/tasks/${taskId}/attachments`);
  },

  /**
   * Загрузить вложение к задаче
   */
  uploadTaskAttachment: async (taskId: string, data: UploadTaskAttachmentRequest): Promise<ApiResponse<TaskAttachment>> => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.description) formData.append('description', data.description);
    
    return httpClient.post(`/tasks/${taskId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Удалить вложение
   */
  deleteTaskAttachment: async (taskId: string, attachmentId: string): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
  },

  // ===== ПЕРСОНАЛЬНЫЕ ЗАДАЧИ =====

  /**
   * Получить мои задачи (созданные мной)
   */
  getMyTasks: async (params?: TaskFilters): Promise<ApiResponse<Task[]>> => {
    try {
      const limit = params?.limit ?? 20;
      const page = params?.page ?? 1;
      const queryParams = {
        status: params?.status,
        priority: params?.priority,
        eventId: params?.eventId,
        search: params?.search,
        skip: (page - 1) * limit,
        take: limit,
      };
      return await httpClient.get(`/tasks/my${buildQueryString(queryParams)}`);
    } catch (error: unknown) {
      const err = error as { response?: unknown; code?: string };
      if (!err.response || err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        return { data: [], success: true };
      }
      throw error;
    }
  },

  /**
   * Получить задачи, назначенные мне
   */
  getAssignedTasks: async (params?: TaskFilters): Promise<ApiResponse<Task[]>> => {
    const limit = params?.limit ?? 20;
    const page = params?.page ?? 1;
    const queryParams = {
      status: params?.status,
      priority: params?.priority,
      eventId: params?.eventId,
      search: params?.search,
      skip: (page - 1) * limit,
      take: limit,
    };
    return httpClient.get(`/tasks/assigned${buildQueryString(queryParams)}`);
  },

  /**
   * Получить задачи, созданные мной (Backend: GET /tasks/my)
   */
  getCreatedTasks: async (params?: TaskFilters): Promise<ApiResponse<Task[]>> => {
    try {
      const limit = params?.limit ?? 20;
      const page = params?.page ?? 1;
      const queryParams = {
        status: params?.status,
        priority: params?.priority,
        eventId: params?.eventId,
        search: params?.search,
        skip: (page - 1) * limit,
        take: limit,
      };
      return await httpClient.get(`/tasks/my${buildQueryString(queryParams)}`);
    } catch (error: unknown) {
      const err = error as { response?: unknown; code?: string };
      if (!err.response || err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        return { data: [], success: true };
      }
      throw error;
    }
  },

  // ===== СТАТИСТИКА И АНАЛИТИКА =====

  /**
   * Получить статистику/обзор задач (Backend: GET /tasks/analytics/overview)
   */
  getTaskStatistics: async (): Promise<ApiResponse<TaskStatistics>> => {
    return httpClient.get('/tasks/analytics/overview');
  },

  /**
   * Экспорт задач
   */
  exportTasks: async (format: 'csv' | 'excel' = 'csv', filters?: TaskFilters): Promise<ApiResponse<{ downloadUrl: string }>> => {
    const queryParams = {
      format,
      status: filters?.status,
      priority: filters?.priority,
      assigneeId: filters?.assigneeId,
      eventId: filters?.eventId,
      dueDateFrom: filters?.dueDateFrom,
      dueDateTo: filters?.dueDateTo,
    };

    return httpClient.get(`/tasks/export${buildQueryString(queryParams)}`);
  },

  // ===== МАССОВЫЕ ОПЕРАЦИИ =====
  // NOT_IMPLEMENTED_BACKEND: bulk endpoints могут вернуть 404; в UI обрабатывать 404 или не вызывать до появления API.

  /**
   * Массовое обновление задач
   */
  bulkUpdateTasks: async (taskIds: string[], updates: Partial<UpdateTaskRequest>): Promise<ApiResponse<Task[]>> => {
    return httpClient.patch('/tasks/bulk', { taskIds, updates });
  },

  /**
   * Массовое удаление задач
   */
  bulkDeleteTasks: async (taskIds: string[]): Promise<ApiResponse<{ message: string; deletedCount: number }>> => {
    return httpClient.delete('/tasks/bulk', { data: { taskIds } });
  },

  /**
   * Массовое назначение задач
   */
  bulkAssignTasks: async (taskIds: string[], assigneeId: string): Promise<ApiResponse<Task[]>> => {
    return httpClient.patch('/tasks/bulk/assign', { taskIds, assigneeId });
  },
};
